/**
 * Loader for YouTube's official IFrame Player API (issue #9's remote
 * play/pause). Raw `iframe.contentWindow.postMessage({event:"command",...})`
 * looks like it should work with just `enablejsapi=1`, but it's unreliable —
 * commands sent before YouTube's own listener attaches are silently dropped,
 * with no error and no retry. The documented, reliable way to control an
 * existing embed is to hand its iframe element to `new YT.Player(...)`, which
 * queues calls until the player actually signals ready.
 */
export interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement,
        opts?: { events?: { onReady?: () => void; onError?: () => void } },
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<NonNullable<Window["YT"]>> | null = null;

/** Injects the API script once (idempotent) and resolves when `window.YT` is ready. */
export function loadYouTubeIframeApi(): Promise<NonNullable<Window["YT"]>> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    // YouTube calls this global by name once the script has loaded — chain
    // onto any callback a second caller may have already registered.
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve(window.YT!);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(script);
  });
  return apiPromise;
}
